import { useAuth } from "../../context/AuthContext";
import { useLayout } from "../../context/LayoutContext";
import { useNavigate, useLocation } from "react-router-dom";
import "../../assets/scss/layout/sidebar.scss";
import {
  menuRoutes,
  logoutRoute,
  hidePanelRoute,
  helpRoute,
  showPanelRoute,
} from "../../lib/dataRoutes";
import UploadProgress from "../ui/progress/UploadProgress";
import { useSatelliteData } from "../../context/SatelliteDataContext";
import LogotipoCTPS from "../../assets/img/logotipo_cootranspetrols.png";
import LogotipoCTPSRecorte from "../../assets/img/logotivo_v2.png";
import { useEffect, useState, useCallback } from "react";
import { SwitchTheme } from "../../layout/SwitchTheme";
import { Tooltip, createTheme } from "@mui/material";
import { Settings } from "lucide-react";
import ChevronDownIcon from "../../assets/svg/layout/panel_left.svg";
import ChevronDownLightIcon from "../../assets/svg/layout/panel_left_light.svg";
import ChevronRightIcon from "../../assets/svg/layout/panel_right.svg";
import ChevronRightLightIcon from "../../assets/svg/layout/panel_right_light.svg";

export default function Sidebar() {
  const { isNavExpanded, toggleNav, theme, toggleTheme } = useLayout();
  const { uploadProgress, resetProgress } = useSatelliteData();
  const { user, logout, selectedRole, changeRole } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [indicatorOffset, setIndicatorOffset] = useState(0);
  const [openFolders, setOpenFolders] = useState({});
  const [hoverFolder, setHoverFolder] = useState(null);
  const [showRoleMenu, setShowRoleMenu] = useState(false);

  // Helper: comprobar si existe una ruta EXACTA en el menú para un pathname dado
  const routeExists = useCallback(
    (pathToCheck) => {
      const routes = menuRoutes[selectedRole?.role_code] || [];
      for (const r of routes) {
        if (r.path === pathToCheck) return true;
        if (r.isFolder && r.children) {
          for (const c of r.children) {
            if (c.path === pathToCheck) return true;
          }
        }
      }
      return false;
    },
    [selectedRole?.role_code]
  );

  // Detectar carpetas que deben estar abiertas basadas en la ruta actual
  const updateOpenFolders = useCallback(() => {
    if (selectedRole?.role_code) {
      const newOpenFolders = { ...openFolders };
      let hasChanges = false;
  
      menuRoutes[selectedRole.role_code]?.forEach(route => {
        if (route.isFolder && route.children) {
          // Verificar si la ruta actual está dentro de alguna subruta
          const hasActiveChild = route.children.some(child => 
            // Verificar si la ruta actual comienza con la ruta del hijo
            location.pathname === child.path || 
            location.pathname.startsWith(`${child.path}/`)
          );
          
          if (hasActiveChild && !newOpenFolders[route.name]) {
            newOpenFolders[route.name] = true;
            hasChanges = true;
          }
        }
      });
      
      if (hasChanges) {
        setOpenFolders(newOpenFolders);
      }
    }
  }, [location.pathname, selectedRole?.role_code, openFolders]);

  // Actualizar el indicador para la ruta activa
  const updateIndicator = useCallback(() => {
    setTimeout(() => {
      const container = document.querySelector('.sidebar-body-routes');
      if (!container) return;

      const isVisible = (el) => {
        if (!el) return false;
        const rect = el.getBoundingClientRect();
        return rect.height > 0 && rect.width > 0;
      };

      // Preferir un elemento de ruta activo que NO sea header de carpeta y
      // que esté visible. Si está dentro de una carpeta, debe estar abierta.
      let activeElement = Array.from(document.querySelectorAll('.sidebar-body-routes-route.active'))
        .find(el => {
          if (el.classList.contains('sidebar-folder-header')) return false;
          const folder = el.closest('.sidebar-folder');
          if (folder) {
            return folder.classList.contains('open') && isVisible(el);
          }
          return isVisible(el);
        });

      // Si no encontramos un hijo activo visible, buscar header de carpeta activo
      if (!activeElement) {
        const folderHeader = document.querySelector('.sidebar-folder-header.active');
        if (folderHeader && isVisible(folderHeader)) {
          activeElement = folderHeader;
        }
      }

      // Si aún no hay elemento activo pero hay un hijo activo dentro de carpeta
      // cerrada, apuntar al header de esa carpeta
      if (!activeElement) {
        const anyActiveChild = Array.from(document.querySelectorAll('.sidebar-body-routes-route.active'))
          .find(el => !el.classList.contains('sidebar-folder-header'));
        if (anyActiveChild) {
          const folder = anyActiveChild.closest('.sidebar-folder');
          if (folder) {
            const header = folder.querySelector('.sidebar-folder-header');
            if (header && isVisible(header)) activeElement = header;
          }
        }
      }

      if (activeElement) {
        const parentOffset = container.getBoundingClientRect().top;
        const activeOffset = activeElement.getBoundingClientRect().top;
        const activeHeight = activeElement.offsetHeight;
        // Ajustar la posición del indicador para que quede centrado verticalmente
        setIndicatorOffset(activeOffset - parentOffset + activeHeight / 2);
      }
    }, 150);
  }, []);

  useEffect(() => {
    updateOpenFolders();
    updateIndicator();
  }, [location.pathname, selectedRole?.role_code, updateOpenFolders, updateIndicator]);

  // Actualizar el indicador cuando se abre/cierra una carpeta
  useEffect(() => {
    updateIndicator();
  }, [openFolders, updateIndicator]);

  const handleNavigation = (path) => {
    navigate(path);
  };

  // Modificar función toggleFolder
  const toggleFolder = (folderName, event) => {
    event.preventDefault();
    event.stopPropagation();
    
    // Toggle explícito sin restricciones
    setOpenFolders(prev => {
      const newState = { ...prev };
      newState[folderName] = !prev[folderName];
      return newState;
    });
    
    // Forzar actualización del indicador después del toggle
    setTimeout(updateIndicator, 50);
  };

  const handleMouseEnter = (folderName) => {
    if (!isNavExpanded) {
      setHoverFolder(folderName);
    }
  };

  const handleMouseLeave = () => {
    if (!isNavExpanded) {
      setHoverFolder(null);
    }
  };

  const getThemeIcon = (darkIcon, lightIcon) => {
    return theme === "dark" ? lightIcon : darkIcon;
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  // Función simplificada para manejar la navegación a ayuda
  const handleHelpNavigation = () => {
    handleNavigation(helpRoute.path);
  };

  const renderRouteItem = (route, index, isChild = false) => {
    const currentIcon = getThemeIcon(route.icon, route.iconLight);
    
    // Verificar si la ruta actual es la activa o es una subruta de la ruta actual
    // Pero si existe una ruta exacta en el menú igual a la ruta actual, evitar marcar
    // a la ruta padre como activa cuando estamos en una ruta hija que también
    // está registrada en el menú.
    const isActive = (() => {
      if (!route.path) return false;
      if (location.pathname === route.path) return true;

      // Si la ruta actual empieza con la ruta del item (es subruta), solo
      // marcar como activa si NO existe una ruta exacta para el pathname
      const exactExists = routeExists(location.pathname);
      return !exactExists && location.pathname.startsWith(`${route.path}/`);
    })();
    
    const button = (
      <button
        key={route.path || `route-${index}`}
        type="button"
        className={`sidebar-body-routes-route ${
          isActive ? "active" : ""
        } ${isChild ? "sidebar-folder-item" : ""}`}
        onClick={() => route.path && handleNavigation(route.path)}
      >
        <div className="sidebar-body-routes-route-icon">
          <img src={currentIcon} alt="" />
        </div>
        {isNavExpanded && (
          <span className="sidebar-body-routes-route-name">
            {route.name}
          </span>
        )}
      </button>
    );
    
    return isNavExpanded ? (
      button
    ) : (
      <Tooltip
        key={`tooltip-${index}`}
        title={route.name}
        placement="right"
        arrow
        sx={{
          backgroundColor: theme === "dark" ? "#2A2D34" : "#ffffff",
          color: theme === "dark" ? "#ffffff" : "#1E1E1E",
          fontSize: "0.875rem",
        }}
      >
        {button}
      </Tooltip>
    );
  };

  const renderFolderItem = (route, index) => {
    const currentIcon = getThemeIcon(route.icon, route.iconLight);
    const isOpen = !!openFolders[route.name]; 
    const isHovered = hoverFolder === route.name;
    
    // Verificar si alguno de los hijos tiene la ruta activa
    // Si existe una ruta exacta en el menú igual al pathname y no es el hijo,
    // no consideramos subpaths como active (para evitar marcar la ruta padre).
    const exactExists = routeExists(location.pathname);
    const hasActiveChild = route.children.some(child =>
      location.pathname === child.path ||
      (!exactExists && child.path && location.pathname.startsWith(`${child.path}/`))
    );
    
    // La carpeta solo se marca como "active" si contiene una ruta activa Y está cerrada
    const isFolderActive = hasActiveChild && !isOpen;
    
    const chevronDown = getThemeIcon(ChevronDownIcon, ChevronDownLightIcon);
    const chevronRight = getThemeIcon(ChevronRightIcon, ChevronRightLightIcon);
    
    return (
      <div 
        key={`folder-${index}`} 
        className={`sidebar-folder ${isOpen ? "open" : ""} ${hasActiveChild ? "has-active-child" : ""}`}
        onMouseEnter={() => handleMouseEnter(route.name)}
        onMouseLeave={handleMouseLeave}
      >
        <button
          type="button"
          className={`sidebar-body-routes-route sidebar-folder-header ${
            isOpen ? "open" : ""
          } ${isFolderActive ? "active" : ""}`}
          onClick={(e) => toggleFolder(route.name, e)}
        >
          <div className="sidebar-body-routes-route-icon">
            <img src={currentIcon} alt="" />
          </div>
          {isNavExpanded && (
            <>
              <span className="sidebar-body-routes-route-name">
                {route.name}
              </span>
              <span className={`sidebar-folder-arrow ${isOpen ? "rotated" : ""}`}>
                <img 
                  src={isOpen ? chevronDown : chevronRight} 
                  alt={isOpen ? "Colapsar" : "Expandir"} 
                  className={isOpen ? "chevron-down" : "chevron-right"}
                />
              </span>
            </>
          )}
        </button>
        
        <div className={`sidebar-folder-content ${(isOpen || (!isNavExpanded && isHovered)) ? "expanded" : ""}`}>
          {route.children.map((childRoute, childIndex) => (
            renderRouteItem(childRoute, `${index}-${childIndex}`, true)
          ))}
        </div>
      </div>
    );
  };

  const renderRoutes = () => {
    const routes = menuRoutes[selectedRole?.role_code] || [];
    
    return routes.map((route, index) => {
      // Si es una carpeta, renderizarla como tal
      if (route.isFolder) {
        return renderFolderItem(route, index);
      }
      
      // Si es una ruta normal
      return renderRouteItem(route, index);
    });
  };

  const tooltipTheme = createTheme({
    components: {
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: theme === 'dark' ? '#2A2D34' : '#ffffff',
            color: theme === 'dark' ? '#ffffff' : '#1E1E1E',
            fontSize: '0.875rem',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            padding: '8px 12px'
          },
          arrow: {
            color: theme === 'dark' ? '#2A2D34' : '#ffffff'
          }
        }
      }
    }
  });

  return (
    <aside
      className={`sidebar ${isNavExpanded ? "expanded" : "collapsed"} ${theme}`}
    >
      {/* HEADER */}
      <div
        className={`sidebar-header ${isNavExpanded ? "expanded" : "collapsed"}`}
      >
        <div className="sidebar-header-content">
          {isNavExpanded ? (
            <img
              src={LogotipoCTPS}
              alt="Company Logo"
              className="sidebar-header-logo"
            />
          ) : (
            <img
              src={LogotipoCTPSRecorte}
              alt="Company Logo"
              className="sidebar-header-logov2"
            />
          )}
        </div>
      </div>

      {/* BODY */}
      <div className="sidebar-body">
        <div
          className="sidebar-body-routes"
          style={{ "--indicator-offset": `${indicatorOffset}px` }}
        >
          {renderRoutes()}
        </div>
      </div>

      {/* FOOTER - Sections remain unchanged */}
      <div className="sidebar-footer">
        <div className="sidebar-footer-upload">
          {/* UploadProgress component */}
          {isNavExpanded ? (
            <UploadProgress
              percentage={uploadProgress.percentage || 0}
              type="linear"
              fileName={uploadProgress.currentFile || "Sin Proceso"}
              uploadLink="/recursos/videos/conferencia"
              estimatedTime={uploadProgress.estimatedTimeRemaining}
              onReset={resetProgress}
              status={uploadProgress.status}
            />
          ) : (
            <UploadProgress
              percentage={uploadProgress.percentage || 0}
              type="small-circle"
              fileName={uploadProgress.currentFile || "sin proceso"}
              uploadLink="/documentos/documento.pdf"
              estimatedTime={uploadProgress.estimatedTimeRemaining}
              onReset={resetProgress}
              status={uploadProgress.status}
            />
          )}
        </div>
      </div>

      {/* FOOTER - Hide panel / Help buttons */}
      <div className="sidebar-footer">
        {/* Hide/Show panel button */}
        {!isNavExpanded ? (
          <Tooltip title={isNavExpanded ? "Ocultar panel" : "Mostrar panel"} placement="right" arrow>
            <button
              type="button"
              className="sidebar-body-routes-route sidebar-hide-panel"
              onClick={toggleNav}
            >
              <div className="sidebar-body-routes-route-icon">
                <img
                  src={getThemeIcon(showPanelRoute.icon, showPanelRoute.iconLight)}
                  alt="Mostrar panel"
                />
              </div>
            </button>
          </Tooltip>
        ) : (
          <button
            type="button"
            className="sidebar-body-routes-route sidebar-hide-panel"
            onClick={toggleNav}
          >
            <div className="sidebar-body-routes-route-icon">
              <img
                src={getThemeIcon(hidePanelRoute.icon, hidePanelRoute.iconLight)}
                alt="Ocultar panel"
              />
            </div>
            <span className="sidebar-body-routes-route-name">
              {hidePanelRoute.name}
            </span>
          </button>
        )}

        {/* Help button simplificado - navegación directa */}
        {!isNavExpanded ? (
          <Tooltip title={helpRoute.name} placement="right" arrow>
            <button
              type="button"
              className="sidebar-body-routes-route"
              onClick={handleHelpNavigation}
            >
              <div className="sidebar-body-routes-route-icon">
                <img
                  src={getThemeIcon(helpRoute.icon, helpRoute.iconLight)}
                  alt={helpRoute.name}
                />
              </div>
            </button>
          </Tooltip>
        ) : (
          <button
            type="button"
            className="sidebar-body-routes-route"
            onClick={handleHelpNavigation}
          >
            <div className="sidebar-body-routes-route-icon">
              <img
                src={getThemeIcon(helpRoute.icon, helpRoute.iconLight)}
                alt={helpRoute.name}
              />
            </div>
            <span className="sidebar-body-routes-route-name">
              {helpRoute.name}
            </span>
          </button>
        )}
      </div>

      {/* FOOTER - User info and logout */}
      <div className="sidebar-footer">
        {/* Logout button */}
        {!isNavExpanded ? (
          <Tooltip title="Cerrar sesión" placement="right" arrow>
            <button
              type="button"
              className="sidebar-body-routes-route"
              onClick={handleLogout}
            >
              <div className="sidebar-body-routes-route-icon">
                <img
                  src={getThemeIcon(logoutRoute.icon, logoutRoute.iconLight)}
                  alt="Cerrar sesión"
                />
              </div>
            </button>
          </Tooltip>
        ) : (
          <button
            type="button"
            className="sidebar-body-routes-route"
            onClick={handleLogout}
          >
            <div className="sidebar-body-routes-route-icon">
              <img
                src={getThemeIcon(logoutRoute.icon, logoutRoute.iconLight)}
                alt="Cerrar sesión"
              />
            </div>
            <span className="sidebar-body-routes-route-name">
              {logoutRoute.name}
            </span>
          </button>
        )}

        {/* User info */}
        {isNavExpanded && (
          <div className="sidebar-footer-user">
            <div className="sidebar-footer-user-role">

              {user?.roles && user.roles.length > 1 && (
                <>
                  {!isNavExpanded ? (
                    <Tooltip title="Cambiar rol" placement="right" arrow>
                      <button
                        type="button"
                        className="sidebar-body-routes-route role-change-btn"
                        onClick={() => setShowRoleMenu(!showRoleMenu)}
                        aria-label="Cambiar rol"
                      >
                        <div className="sidebar-body-routes-route-icon">
                          <Settings size={18} />
                        </div>
                      </button>
                    </Tooltip>
                  ) : (
                    <button
                      type="button"
                      className="sidebar-body-routes-route role-change-btn"
                      onClick={() => setShowRoleMenu(!showRoleMenu)}
                      aria-haspopup="menu"
                      aria-expanded={showRoleMenu}
                    >
                      <div className="sidebar-body-routes-route-icon">
                        <Settings size={18} />
                      </div>
                      <span className="sidebar-body-routes-route-name">Cambiar rol</span>
                    </button>
                  )}
                </>
              )}

              {showRoleMenu && user?.roles && user.roles.length > 1 && (
                <div className="role-menu">
                  {user.roles.map((role) => (
                    <button
                      key={role.id}
                      className={`role-menu-item ${selectedRole?.id === role.id ? 'active' : ''}`}
                      onClick={() => {
                        changeRole(role);
                        setShowRoleMenu(false);
                      }}
                    >
                      {role.name}
                      {role.id === user.primary_role?.id && <span className="primary-badge">Principal</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Theme switch */}
        {!isNavExpanded ? (
          <Tooltip title="Cambiar tema" placement="right" arrow>
            <button
              type="button"
              className="sidebar-body-routes-route theme-switch"
              onClick={toggleTheme}
            >
              <div className="sidebar-body-routes-route-icon">
                <SwitchTheme
                  checked={theme === "dark"}
                  onChange={toggleTheme}
                  sx={{
                    m: 0,
                    "& .MuiSwitch-switchBase": {
                      "&.Mui-checked": {
                        transform: "translateX(22px)",
                      },
                    },
                  }}
                />
              </div>
            </button>
          </Tooltip>
        ) : (
          <button
            type="button"
            className="sidebar-body-routes-route theme-switch"
            onClick={toggleTheme}
          >
            <div className="sidebar-body-routes-route-icon">
              <SwitchTheme
                checked={theme === "dark"}
                onChange={toggleTheme}
                sx={{
                  m: 0,
                  "& .MuiSwitch-switchBase": {
                    "&.Mui-checked": {
                      transform: "translateX(22px)",
                    },
                  },
                }}
              />
            </div>
            <span className="sidebar-body-routes-route-name">Cambiar tema</span>
          </button>
        )}
      </div>
    </aside>
  );
}